// src/pages/SessionUnavailablePage.jsx
//
// Terminal state for "we could not load your profile."
//
// ── WHY THIS EXISTS RATHER THAN A DEFAULT ────────────────────────────────────
//
// Where a user lands is derived from their profile. When /auth/me fails there
// is no profile, and both defaults are wrong:
//
//   fail open  → the platform. For an analyst that is a shell where every call
//                403s, so it renders essentially blank. Correct gating, and it
//                reads as a broken application. Someone hits that and files a
//                bug against the permissions system, and the control gets
//                removed by whoever is on support.
//
//   fail closed → some guessed home. Sending a person somewhere the server
//                never confirmed is a different wrong answer, not a safer one.
//
// So: say what happened and stop. ACCESS_BOUNDARY §6.
//
// ── WHY IT CANNOT LOOP ───────────────────────────────────────────────────────
//
// §5 of that document defends fail-open in the UI specifically because a UI
// that fails closed on a transient profile fetch produces a redirect loop. That
// objection does not apply here: this route's beforeLoad is requireAuth only —
// it never consults the profile — so arriving here cannot bounce anywhere, and
// requireAuthAndAllowedRoute returns early when it is already the destination.
//
// Retry is a full reload rather than a router navigation, because the failure
// is usually a dead token or a cold API and a reload re-runs Keycloak init as
// well as the fetch.
import { AlertTriangle } from "lucide-react";

export default function SessionUnavailablePage() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-bg px-6 text-fg-primary">
      <div className="max-w-md">
        <div className="flex items-center gap-2 text-amber-400">
          <AlertTriangle size={18} />
          <h1 className="text-base font-medium tracking-tight">
            Couldn&rsquo;t load your profile
          </h1>
        </div>

        <p className="mt-3 text-sm text-fg-secondary">
          You are signed in, but we could not read which workspace you belong
          to, so we have not sent you anywhere rather than guess.
        </p>

        <p className="mt-2 text-sm text-fg-secondary">
          This is usually temporary. If it persists, your account may not have
          finished being set up — an administrator can check it.
        </p>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded border border-accent-action/40 bg-accent-action/15 px-4 py-2 text-sm text-accent-action transition-colors hover:bg-accent-action/25"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() =>
              window.__keycloak?.logout({ redirectUri: window.location.origin })
            }
            className="text-sm text-fg-secondary transition-colors hover:text-fg-primary"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
